<template>
	<section
		ref="sectionRef"
		class="teacher-consultation"
		:class="{ 'teacher-consultation--en': locale === 'en' }"
		aria-label="老師諮詢"
	>
		<div class="title">
			<img src="../../../images/pc/left2.png" class="pic" alt="" />
			{{ t("index.teacherConsultation.title") }}
			<img src="../../../images/pc/right2.png" class="pic" alt="" />
		</div>
		<div class="title2">{{ t("index.teacherConsultation.title2") }}</div>
		<div
			ref="innerRef"
			class="teacher-consultation__inner"
			:class="{ 'teacher-consultation__inner--dragging': isDragging }"
			@scroll="handleScroll"
			@pointerdown="onPointerDown"
			@pointermove="onPointerMove"
			@pointerup="onPointerUp"
			@pointercancel="onPointerUp"
			@dragstart.prevent
		>
			<article
				v-for="(teacher, index) in teacherList"
				:key="teacher.uid || teacher.id"
				:ref="el => bindCardRef(el, teacher, index)"
				class="teacher-consultation__card"
			>
				<div class="teacher-consultation__image-box" @click="handleConsult(teacher, index)">
					<img
						class="teacher-consultation__image"
						:src="teacher.avatar"
						:alt="teacher.name"
						draggable="false"
					/>
					<div class="teacher-consultation__rating">
						<img :src="ratingIcon" alt="" aria-hidden="true" draggable="false" />
						<span>{{ teacher.rating }}</span>
					</div>
				</div>
				<div class="teacher-consultation__content">
					<h3 class="teacher-consultation__name">{{ teacher.name }}</h3>
					<div class="teacher-consultation__tags">
						<span
							v-for="(tag, tagIndex) in teacher.tagList.slice(0, 3)"
							:key="`${tag}-${tagIndex}`"
							class="teacher-consultation__tag"
						>
							{{ tag }}
						</span>
					</div>
					<div class="teacher-consultation__footer">
						<p class="teacher-consultation__summary">{{ teacher.summaryText }}</p>
						<button
							class="teacher-consultation__button"
							type="button"
							@click="handleConsult(teacher, index)"
						>
							{{
								teacher.area_type === 10
									? t("index.teacherConsultation.consultButton")
									: t("index.teacherConsultation.consultButton2")
							}}
						</button>
					</div>
				</div>
			</article>
		</div>
		<div
			v-if="showDots"
			class="teacher-consultation__dots"
			role="tablist"
			aria-label="老師列表分頁"
		>
			<button
				v-for="index in dotCount"
				:key="index - 1"
				type="button"
				class="teacher-consultation__dot"
				:class="{ 'teacher-consultation__dot--active': activeDot === index - 1 }"
				:aria-label="`第 ${index} 頁`"
				:aria-selected="activeDot === index - 1"
				role="tab"
				@click="scrollToDot(index - 1)"
			/>
		</div>
	</section>
</template>

<script setup>
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "~/composables/useI18n";
import { getTeacherAskRecommendList } from "~/api/api";
import { buildOssImageUrl } from "~/utils/oss-image";
import {
	formatMentorTags,
	useHomepageTracking,
	useMentorCardImpression,
	useSectionView,
} from "@variants/index/composables/useHomepageTracking";
import ratingIcon from "../../../images/pc/start.png";

const VISIBLE_CARD_COUNT = 5;

const { t, locale } = useI18n();
const { track } = useHomepageTracking("pc");
const { isLogin } = storeToRefs(useUserStore());
const openLoginPopup = inject("openLoginPopup", null);
const sectionRef = ref(null);
const innerRef = ref(null);
const activeDot = ref(0);
const isDragging = ref(false);
let dragStartX = 0;
let dragStartScrollLeft = 0;
let dragMoved = false;
let activePointerId = null;
const { bindCardRef } = useMentorCardImpression({ track, sectionId: "global_mentors" });

useSectionView(sectionRef, {
	track,
	sectionId: "global_mentors",
});

const props = defineProps({
	teachers: {
		type: Array,
		default: () => [],
	},
});

const RESOURCE_SIZE = 6;

const { data: ipTeacherData } = await useLazyAsyncData(
	() => `home-teacher-consultation-pc-${locale.value}`,
	async () => {
		const res = await getTeacherAskRecommendList({
			page: 1,
			size: RESOURCE_SIZE,
		});
		return (res?.data || []).map(formatIpTeacher);
	},
	{
		default: () => [],
		server: true,
		watch: [locale],
	}
);

const teacherList = computed(() => {
	const source = ipTeacherData.value.length ? ipTeacherData.value : props.teachers;
	return source.filter(Boolean);
});

const pageStarts = computed(() => {
	const total = teacherList.value.length;
	const visible = VISIBLE_CARD_COUNT;
	if (total <= visible) return [0];

	const maxStart = total - visible;
	const starts = [0];
	let next = visible;
	while (next < maxStart) {
		starts.push(next);
		next += visible;
	}
	if (starts[starts.length - 1] !== maxStart) {
		starts.push(maxStart);
	}
	return starts;
});

const dotCount = computed(() => pageStarts.value.length);

const showDots = computed(() => dotCount.value > 1);

function getScrollStep() {
	const container = innerRef.value;
	if (!container) return 0;
	const firstCard = container.querySelector(".teacher-consultation__card");
	if (!firstCard) return 0;
	const styles = window.getComputedStyle(container);
	const gap = Number.parseFloat(styles.columnGap || styles.gap || "0") || 16;
	return firstCard.offsetWidth + gap;
}

const handleScroll = () => {
	const container = innerRef.value;
	const step = getScrollStep();
	const starts = pageStarts.value;
	if (!container || !step || !starts.length) return;

	const scrollLeft = container.scrollLeft;
	let closest = 0;
	let minDist = Infinity;
	starts.forEach((start, index) => {
		const dist = Math.abs(scrollLeft - start * step);
		if (dist < minDist) {
			minDist = dist;
			closest = index;
		}
	});
	activeDot.value = closest;
};

const scrollToDot = index => {
	const container = innerRef.value;
	const step = getScrollStep();
	const start = pageStarts.value[index];
	if (!container || !step || start == null) return;

	activeDot.value = index;
	container.scrollTo({
		left: start * step,
		behavior: "smooth",
	});
};

function onPointerDown(event) {
	const container = innerRef.value;
	if (!container || event.button !== 0) return;
	if (event.target?.closest?.("button, a, input, textarea, select")) return;

	activePointerId = event.pointerId;
	dragStartX = event.clientX;
	dragStartScrollLeft = container.scrollLeft;
	dragMoved = false;
	isDragging.value = false;
}

function onPointerMove(event) {
	const container = innerRef.value;
	if (!container || activePointerId !== event.pointerId) return;

	const distance = event.clientX - dragStartX;
	if (!dragMoved && Math.abs(distance) < 4) return;

	if (!dragMoved) {
		container.setPointerCapture(event.pointerId);
	}
	dragMoved = true;
	isDragging.value = true;
	container.scrollLeft = dragStartScrollLeft - distance;
	event.preventDefault();
}

function onPointerUp(event) {
	const container = innerRef.value;
	if (!container || activePointerId !== event.pointerId) return;

	if (container.hasPointerCapture(event.pointerId)) {
		container.releasePointerCapture(event.pointerId);
	}
	activePointerId = null;
	isDragging.value = false;
}

function formatIpTeacher(teacher) {
	return {
		...teacher,
		id: teacher?.uid || teacher?.teacher_uid || teacher?.id,
		uid: teacher?.uid || "",
		name: teacher?.nickname || t("index.teacherConsultation.defaultName"),
		tagList: normalizeTags(teacher),
		avatar: buildOssImageUrl(teacher?.avatar || "", { strategy: "avatar", w: 240, h: 240, q: 80 }),
		rating: formatScore(teacher?.score),
		summaryText: formatSummary(teacher),
	};
}

function getDefaultTags() {
	return [
		t("index.teacherConsultation.defaultTags.emotion"),
		t("index.teacherConsultation.defaultTags.wealth"),
		t("index.teacherConsultation.defaultTags.life"),
	];
}

function normalizeTags(teacher) {
	const source = teacher?.label;
	const tags = Array.isArray(source)
		? source
				.map(item => (typeof item === "string" ? item : item?.text || ""))
				.map(text => String(text).trim())
				.filter(Boolean)
				.slice(0, 3)
		: [];
	return tags.length ? tags : getDefaultTags();
}

function formatScore(value) {
	const score = Number(value);
	if (!score) return "5.0";
	return Math.min(score, 5).toFixed(1);
}

function formatSummary(teacher) {
	const parts = [];
	if (teacher?.year) {
		parts.push(t("index.teacherConsultation.summary.years", { year: teacher.year }));
	}
	const answers = Number(teacher?.answers) || 0;
	if (answers > 0) {
		const users = answers >= 1000 ? `${Math.floor(answers / 1000)}K+` : `${answers}+`;
		parts.push(t("index.teacherConsultation.summary.users", { users }));
	}
	return parts.join(" · ") || t("index.teacherConsultation.summary.fallback");
}

function getTeacherActionUrl(teacher) {
	const teacherId = teacher?.uid || teacher?.id;

	if (Number(teacher?.area_type) === 10) {
		return teacherId ? `/seer/teacher-detail/${teacherId}` : "/seer/master-fortune";
	}

	return teacherId ? `/seer/chat/${teacherId}` : "/seer/master-fortune";
}

function handleConsult(teacher, index) {
	if (dragMoved) {
		dragMoved = false;
		return;
	}

	track("mentor_consult_click", {
		mentor_id: String(teacher.uid || teacher.id || ""),
		mentor_name: teacher.name,
		position: index + 1,
		mentor_tags: formatMentorTags(teacher.tagList),
		rating: Number.parseFloat(teacher.rating) || undefined,
	});

	const targetUrl = getTeacherActionUrl(teacher);
	const isImmediateConsult = Number(teacher?.area_type) !== 10;

	if (isImmediateConsult && !isLogin.value) {
		if (typeof openLoginPopup === "function") {
			openLoginPopup(() => {
				navigateTo(targetUrl);
			});
			return;
		}
		navigateTo("/seer/login");
		return;
	}

	navigateTo(targetUrl);
}
</script>

<style lang="scss" scoped>
.teacher-consultation {
	width: 100%;
	max-width: 100%;
	background: #ffffff;
	overflow: hidden;
	margin-top: 100px;
	.title {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 20px;
		font-size: 32px;
		color: #333333;
		font-weight: bold;

		.pic {
			width: 120px;
			height: 16px;
		}
	}

	.title2 {
		font-size: 24px;
		color: #333333;
		text-align: center;
		margin: 10px 0 50px;
		font-weight: 500;
	}

	&__inner {
		display: flex;
		width: 100%;
		max-width: 100%;
		gap: 16px;
		overflow-x: auto;
		overflow-y: hidden;
		scroll-snap-type: x proximity;
		scroll-behavior: smooth;
		-webkit-overflow-scrolling: touch;
		scrollbar-width: none;
		-ms-overflow-style: none;
		cursor: grab;
		touch-action: pan-x;
		user-select: none;

		&::-webkit-scrollbar {
			display: none;
		}

		&--dragging {
			cursor: grabbing;
			scroll-snap-type: none;
			scroll-behavior: auto;

			img {
				pointer-events: none;
			}
		}
	}

	&__dots {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 8px;
		margin-top: 24px;
	}

	&__dot {
		width: 8px;
		height: 8px;
		padding: 0;
		border: none;
		border-radius: 999px;
		background: #d9d9d9;
		cursor: pointer;
		transition:
			width 0.25s ease,
			background-color 0.25s ease;

		&--active {
			width: 24px;
			background: #f2af19;
		}
	}

	&__card {
		flex: 0 0 calc((100% - 64px) / 5);
		display: flex;
		flex-direction: column;
		background: #ffffff;
		border: 1px solid #f0ebe6;
		border-radius: 16px;
		overflow: hidden;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
		scroll-snap-align: start;
		min-width: 0;
		box-sizing: border-box;
	}

	&__image-box {
		cursor: pointer;
		position: relative;
		height: 220px;
		background: #fef9ed;
		overflow: hidden;
	}

	&__image {
		display: block;
		width: 170px;
		height: 170px;
		object-fit: cover;
		position: absolute;
		left: 50%;
		bottom: 0;
		transform: translateX(-50%);
		-webkit-user-drag: none;
		user-select: none;
		pointer-events: none;
	}

	&__rating {
		position: absolute;
		top: 12px;
		left: 12px;
		display: inline-flex;
		align-items: center;
		gap: 4px;
		height: 28px;
		padding: 0 10px;
		background: #ffecbc;
		border-radius: 6px;
		font-size: 16px;
		line-height: 20px;
		color: #333;

		img {
			width: 18px;
			height: 18px;
			flex-shrink: 0;
			-webkit-user-drag: none;
			pointer-events: none;
		}
	}

	&__content {
		flex: 1;
		display: flex;
		flex-direction: column;
		padding: 10px 14px;
		background: #ffffff;
	}

	&__name {
		font-size: 20px;
		line-height: 30px;
		color: #333;
		font-weight: normal;
	}

	&__tags {
		display: flex;
		flex-wrap: nowrap;
		gap: 3px;
		margin: 10px 0;
		min-height: 28px;
		overflow: hidden;
	}

	&__tag {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: 1;
		min-width: 0;
		max-width: calc((100% - 6px) / 3);
		height: 28px;
		padding: 0 6px;
		background: #f3f4f6;
		border-radius: 999px;
		font-size: 12px;
		line-height: 18px;
		color: #666666;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		border: 1px solid #f3f4f6;
	}

	&--en {
		.teacher-consultation__tags {
			flex-wrap: wrap;
			gap: 6px;
			overflow: visible;
		}

		.teacher-consultation__tag {
			flex: initial;
			min-width: auto;
			max-width: 100%;
			padding: 0 8px;
		}

		.teacher-consultation__footer {
			margin-top: auto;
			padding-top: 10px;
		}

		.teacher-consultation__summary {
			margin-top: 0;
		}
	}

	&__footer {
		width: 100%;
	}

	&__summary {
		font-size: 13px;
		line-height: 20px;
		color: #999999;
	}

	&__button {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 44px;
		border: 0;
		border-radius: 8px;
		background: #333;
		font-size: 20px;
		color: #ffffff;
		cursor: pointer;
		margin-top: 10px;

		&:hover {
			opacity: 0.7;
		}
	}
}
</style>
